import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from 'prop-types';
import { useNavigate } from "react-router-dom";
import { TableBody, TableCell, TableHead, TableRow, TableContainer, Table, IconButton, Typography, Box, Button, TextField, Chip, InputAdornment, TableSortLabel, Stack, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery, useTheme, Card, CardContent, Divider, Collapse, FormControl, InputLabel, Select, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import { getEcomByFilter, updateEcom } from "../../services/vendorServices";
import { getCustomerBalance, addVendorCredit, getVendorTransactions } from "../../services/billingServices";
import ImpersonateDialog from "./ImpersonateDialog";
import UpDownIcon from "../../assets/icons/updown.png";
import ActionDialog from "../Shared/ActionDialog";
import { useUI } from "../Context/UIContext";
import { useVendorIndexPolling } from "../Context/vendorIndexPollingContext";
import TableLoadingOverlay from "../Shared/TableLoadingOverlay";
import {
  formatValue, formatDate, getStatusLabel, getStatusColor
} from "../../utils/formatters";
import AdminMergedTable from "../Shared/AdminMergedTable";
import AdminTablePagination from "../Shared/AdminTablePagination";
import AdminPageHeader, { adminPageShellSx } from "../Shared/AdminPageHeader";
import ScrollTopFab from "../Shared/ScrollTopFab";
import { mergedTableHeadSx, mergedTableRowSx } from "../../utils/adminTableScrollSx";
import { usePermissions } from "../Context/permissionsProvider";
import ActionIconButton from "../Shared/ActionIconButton";




const TruncatedCellContent = ({ value, maxWidth = '100%', title }) => (
  <Tooltip title={title || value || ""}>
    <Box component="span" sx={{
      display: 'inline-block',
      maxWidth,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle'
    }}>
      {formatValue(value)}
    </Box>
  </Tooltip>
);

TruncatedCellContent.propTypes = {
  value: PropTypes.any,
  maxWidth: PropTypes.string,
  title: PropTypes.string
};

// Costruisce la sorgente immagine dal logo base64 (con o senza prefisso data URL)
const vendorLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith("data:") ? logo : `data:image/jpeg;base64,${logo}`;
};

// Logo vendor con alone "ambient" che sfuma nel bianco; senza logo mostra un monogramma
const AmbientVendorLogo = ({ logo, name, size = 64 }) => {
  const [error, setError] = useState(false);
  const src = vendorLogoSrc(logo);

  if (!src || error) {
    const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
    return (
      <Box sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(145deg, #0e4a9e 0%, #0b3d87 100%)",
        color: "#ffffff",
        fontWeight: 800,
        fontSize: "1.5rem",
        boxShadow: "0 6px 16px rgba(11, 61, 135, 0.22)"
      }}>
        {initial}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {/* Alone ambient ritagliato: non invade il testo, il velo bianco maschera il bordo */}
      <Box sx={{ position: "absolute", inset: -8, borderRadius: "18px", overflow: "hidden" }}>
        <Box
          component="img"
          src={src}
          alt=""
          aria-hidden="true"
          onError={() => setError(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(15px) saturate(1.5)",
            opacity: 0.45
          }}
        />
        <Box sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(closest-side, rgba(255,255,255,0) 30%, #ffffff 100%)"
        }} />
      </Box>
      <Box
        component="img"
        src={src}
        alt={name}
        onError={() => setError(true)}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          borderRadius: "12px"
        }}
      />
    </Box>
  );
};

AmbientVendorLogo.propTypes = {
  logo: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.number
};

// Riga etichetta/valore per le card mobile vendor
const VendorInfoRow = ({ label, value, truncate }) => (
  <Box sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 1.5,
    py: 0.7,
    borderBottom: "1px solid #f1f5f9"
  }}>
    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" sx={{
      fontSize: "0.8rem",
      color: "#1e293b",
      fontWeight: 500,
      textAlign: "right",
      ...(truncate ? { maxWidth: "190px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : { minWidth: 0, wordBreak: "break-word" })
    }}>
      {value}
    </Typography>
  </Box>
);

VendorInfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  truncate: PropTypes.bool
};

// Mostra il logo del vendor in un tooltip sul nome; senza logo restituisce i figli invariati
const LogoTooltip = ({ logo, name, children }) => {
  const src = vendorLogoSrc(logo);
  if (!src) return children;
  return (
    <Tooltip
      arrow
      placement="top"
      copyText={name}
      title={
        <Box sx={{ textAlign: 'center', p: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>{name}</Typography>
          <Box
            component="img"
            src={src}
            alt={name}
            sx={{ maxWidth: 200, maxHeight: 140, objectFit: 'contain', backgroundColor: '#fff', borderRadius: 1, display: 'block' }}
          />
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

LogoTooltip.propTypes = {
  logo: PropTypes.string,
  name: PropTypes.string,
  children: PropTypes.node.isRequired
};

const renderProductProgressBar = (verifiedCount, totalCount) => {
  const total = Number(totalCount) || 0;
  const verified = Number(verifiedCount) || 0;
  const percentage = total > 0 ? Math.min(100, Math.round((verified / total) * 100)) : 0;
  const fractionText = `${verified.toLocaleString('it-IT')} / ${total.toLocaleString('it-IT')}`;

  return (
    <Tooltip title={fractionText} arrow placement="top">
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: '100px', gap: 0.5, cursor: 'pointer' }}>
        {/* Centered Percentage Label above the progress bar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#0b3d87' }}>
            {percentage}%
          </Typography>
        </Box>
        {/* Original thin 6px progress bar */}
        <Box sx={{
          width: '100%',
          height: '6px',
          backgroundColor: '#e2e8f0',
          borderRadius: '3px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{
            width: `${percentage}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #3b82f6 0%, #0b3d87 100%)',
            borderRadius: '3px',
            transition: 'width 0.5s ease-in-out'
          }} />
        </Box>
      </Box>
    </Tooltip>
  );
};

const EmptyStateContent = ({ fetchError, errorMessage, onRetry, onClearFilters, hasSearch }) => (
  <>
    {fetchError ? (
      <>
        <Box sx={{ backgroundColor: '#fff5f5', p: 2, borderRadius: '50%', display: 'flex' }}>
          <CloudOffIcon sx={{ fontSize: 60, color: '#f44336' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Oops! Qualcosa è andato storto</Typography>
        <Typography variant="body1" sx={{ color: '#718096', maxWidth: '400px', textAlign: 'center' }}>
          {errorMessage || "Servizio non raggiungibile. Controlla la connessione."}
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRetry} sx={{ bgcolor: '#0b3d87' }}>
          Riprova
        </Button>
      </>
    ) : (
      <>
        <Box sx={{ backgroundColor: '#f7fafc', p: 2, borderRadius: '50%', display: 'flex' }}>
          <SearchOffIcon sx={{ fontSize: 60, color: '#a0aec0' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Nessun E-commerce trovato</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Prova a modificare i filtri di ricerca
        </Typography>
        {hasSearch && (
          <Button variant="outlined" onClick={onClearFilters}>
            Pulisci filtri
          </Button>
        )}
      </>
    )}
  </>
);

EmptyStateContent.propTypes = {
  fetchError: PropTypes.bool,
  errorMessage: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  hasSearch: PropTypes.bool
};

const VendorActionGroup = ({ row, isMobile, handlers, navigate }) => {
  const { isActionVisible } = usePermissions();
  const {
    handleAccept, handleOpen, handleOpenActionModal,
    handleOpenActionsMenu, handleReactivate, handleOpenImpersonateDialog,
    handleViewCredit
  } = handlers;

  const statusLabel = getStatusLabel(row);
  const isAccepted = ["Verificato", "Registrato"].includes(statusLabel);
  const isAttivo = statusLabel === "Attivo";
  const isRegistrato = statusLabel === "Registrato";

  return (
    <>
      {isAccepted
        ? isActionVisible("gestioneEcommerce", "attiva") && (
          <ActionIconButton
            onClick={() => handleAccept(row)}
            disabled={isRegistrato}
            icon={CheckCircleOutlineIcon}
            color="#4caf50"
            mobileBgColor="#f0fff0"
            isMobile={isMobile}
            title="Attiva"
          />
        )
        : isActionVisible("gestioneEcommerce", "storicoAzioni") && (
          <ActionIconButton
            onClick={() => navigate('/storico-azioni', { state: { vendorId: row.vendorListId, businessName: row.tradeName } })}
            icon={DescriptionOutlinedIcon}
            color="#ffc107"
            mobileBgColor="#fffdf0"
            isMobile={isMobile}
            title="Storico Azioni"
          />
        )}

      {isActionVisible("gestioneEcommerce", "modifica") && (
        <ActionIconButton
          onClick={() => handleOpen(row)}
          icon={EditOutlinedIcon}
          color="#0b3d87"
          mobileBgColor="#eef2ff"
          isMobile={isMobile}
          title="Modifica"
        />
      )}

      {isActionVisible("gestioneEcommerce", "impersona") && (
        <ActionIconButton
          onClick={() => handleOpenImpersonateDialog(row)}
          disabled={!isAttivo}
          icon={VisibilityOutlinedIcon}
          color="#2196f3"
          mobileBgColor="#e3f2fd"
          isMobile={isMobile}
          title="Impersonifica"
        />
      )}

      {isAccepted
        ? isActionVisible("gestioneEcommerce", "rifiuta") && (
          <ActionIconButton
            onClick={() => handleOpenActionModal(row, "Rifiuta", "Non Attivo")}
            disabled={isRegistrato}
            icon={HighlightOffIcon}
            color="#f44336"
            mobileBgColor="#fff0f0"
            isMobile={isMobile}
            title="Rifiuta"
          />
        )
        : (
          <StatusSpecificAction
            row={row} isMobile={isMobile} handleOpenActionsMenu={handleOpenActionsMenu}
            handleOpenActionModal={handleOpenActionModal} handleReactivate={handleReactivate}
          />
        )}

      {isActionVisible("gestioneEcommerce", "credito") && (
        <ActionIconButton
          onClick={() => handleViewCredit(row)}
          icon={PaymentsIcon}
          color="#18c7a4"
          mobileBgColor="#e8fcf7"
          isMobile={isMobile}
          title="Visualizza Credito"
        />
      )}

    </>
  );
};

const StatusSpecificAction = ({ row, isMobile, handleOpenActionModal, handleReactivate, handleOpenActionsMenu }) => {
  const { isActionVisible } = usePermissions();
  const statusLabel = getStatusLabel(row);
  if (statusLabel === "Attivo") {
    if (!isActionVisible("gestioneEcommerce", "sospendi")) return null;
    return (
      <ActionIconButton
        onClick={() => handleOpenActionModal(row, "Sospendi", "Sospeso")}
        icon={BlockIcon}
        color="#ff9800"
        mobileBgColor="#fff5e6"
        isMobile={isMobile}
        title="Sospendi"
      />
    );
  }
  if (statusLabel === "Disattivo") {
    if (!isActionVisible("gestioneEcommerce", "riattiva")) return null;
    return (
      <ActionIconButton
        onClick={() => handleReactivate(row)}
        icon={RestoreIcon}
        color="#4caf50"
        mobileBgColor="#f0fff0"
        isMobile={isMobile}
        title="Riattiva"
      />
    );
  }
  if (!isActionVisible("gestioneEcommerce", "altreAzioni")) return null;
  return (
    <IconButton
      onClick={(e) => handleOpenActionsMenu(e, row)}
      sx={{ backgroundColor: isMobile ? '#f5f5f5' : 'transparent' }}
      size={isMobile ? "small" : "medium"}
    >
      <img
        src={UpDownIcon}
        alt="Opzioni"
        style={{ width: isMobile ? '18px' : '22px', height: isMobile ? '18px' : '22px' }}
      />
    </IconButton>
  );
};

StatusSpecificAction.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  handleOpenActionModal: PropTypes.func.isRequired,
  handleReactivate: PropTypes.func.isRequired,
  handleOpenActionsMenu: PropTypes.func.isRequired
};

VendorActionGroup.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  handlers: PropTypes.shape({
    handleAccept: PropTypes.func.isRequired,
    handleOpen: PropTypes.func.isRequired,
    handleOpenActionModal: PropTypes.func.isRequired,
    handleOpenActionsMenu: PropTypes.func.isRequired,
    handleReactivate: PropTypes.func.isRequired,
    handleOpenImpersonateDialog: PropTypes.func.isRequired,
    handleViewCredit: PropTypes.func.isRequired
  }).isRequired,
  navigate: PropTypes.func.isRequired
};

const EcommerceStats = ({ totalRecordsSum, totalVerifiedSum, totalDaClassificareSum, totalNonValidiSum, isMobile }) => {
  // Ogni metrica ha un colore semantico: un pallino colorato (con alone tenue) la rende
  // riconoscibile a colpo d'occhio. Tolto il prefisso ripetuto "Prodotti": il contesto
  // è già chiaro e le etichette risultano più pulite.
  const items = [
    { label: "Prodotti Totali", value: totalRecordsSum, color: "#16a34a" },
    { label: "Prodotti Classificati", value: totalVerifiedSum, color: "#0284c7" },
    { label: "Prodotti Da Classificare", value: totalDaClassificareSum, color: "#d97706" },
    { label: "Prodotti Bloccati / Errati / Sospesi", value: totalNonValidiSum, color: "#e11d48" },
  ];
  return (
    <Box display="flex" justifyContent="center" sx={{ width: "100%", mt: 1.5, mb: 0.5 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={isMobile ? 1.25 : 2}
        divider={isMobile ? undefined : <Divider orientation="vertical" flexItem sx={{ borderColor: "#e2e8f0" }} />}
        sx={{
          backgroundColor: "#f8fafc",
          px: isMobile ? 2 : 3,
          py: isMobile ? 1.5 : 1,
          borderRadius: "15px",
          border: "1px solid #e2e8f0",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)",
          width: isMobile ? "90%" : "fit-content",
          mx: "auto",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {items.map((it) => (
          <Box key={it.label} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: it.color,
                boxShadow: `0 0 0 3px ${it.color}1a`,
              }}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {it.label}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: it.color }}>
              {it.value.toLocaleString('it-IT')}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

EcommerceStats.propTypes = {
  totalRecordsSum: PropTypes.number.isRequired,
  totalVerifiedSum: PropTypes.number.isRequired,
  totalDaClassificareSum: PropTypes.number.isRequired,
  totalNonValidiSum: PropTypes.number.isRequired,
  isMobile: PropTypes.bool
};

// Etichetta leggibile dello stato di una transazione Stripe
const getTxStatusLabel = (status) => {
  if (status === 'succeeded') return 'Pagato';
  if (status === 'processing') return 'In elaborazione';
  if (status === 'canceled') return 'Annullato';
  return status;
};

// Colore del chip in base allo stato della transazione
const getTxStatusColor = (status) => {
  if (status === 'succeeded') return 'success';
  if (status === 'canceled') return 'error';
  return 'warning';
};

// Contenuto del tab Transazioni del dialog credito
const TransactionsTabContent = ({ loading, error, transactions }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>;
  }
  if (transactions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">Nessuna transazione trovata per questo vendor.</Typography>
      </Box>
    );
  }
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Importo</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Stato</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Descrizione</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx) => (
            <TableRow key={tx.id} hover>
              <TableCell>{tx.created ? new Date(tx.created * 1000).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>€ {(tx.amount / 100).toFixed(2)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={getTxStatusLabel(tx.status)}
                  color={getTxStatusColor(tx.status)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{tx.description || '-'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>{tx.id?.substring(0, 18)}...</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

TransactionsTabContent.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  transactions: PropTypes.array.isRequired
};

// Cella "Referente": mostra nome e cognome su due righe
const ReferenteCell = ({ name: rawName, surname: rawSurname }) => {
  const name = (rawName || "").trim();
  const surname = (rawSurname || "").trim();
  if (!name && !surname) return <>-</>;
  return (
    <Box sx={{ lineHeight: 1.2, display: "inline-block", textAlign: "center", maxWidth: "100%" }} title={`${name} ${surname}`.trim()}>
      {name && <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Box>}
      {surname && <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.75rem", color: "#64748b", fontWeight: 600 }}>{surname}</Box>}
    </Box>
  );
};

ReferenteCell.propTypes = {
  name: PropTypes.string,
  surname: PropTypes.string
};

// Cella "Ultimo import": separa data e ora su due righe
const LastImportCell = ({ value }) => {
  const d = formatDate(value);
  if (d === "-" || !d.includes(", ")) return <>{d}</>;
  const [date, time] = d.split(", ");
  return (
    <Box sx={{ lineHeight: 1.2, display: "inline-block", textAlign: "center" }}>
      <Box>{date}</Box>
      <Box sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600 }}>{time}</Box>
    </Box>
  );
};

LastImportCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array])
};

const getReferenteName = (row) => `${row.referringName || ""} ${row.referringSurname || ""}`.trim() || "-";



const GestioneEcommerce = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Stato per l'espansione delle card su mobile
  const [expandedCards, setExpandedCards] = useState({});

  // Caricamento iniziale dei filtri da sessionStorage
  const getInitialFilters = () => {
    const saved = sessionStorage.getItem("ecommerce_filters");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing filters from sessionStorage:", e);
      }
    }
    return {};
  };

  const initialFilters = getInitialFilters();

  const [filterInput, setFilterInput] = useState(initialFilters.query || "");
  const filterRef = useRef(null);
  const [ecommerceList, setEcommerceList] = useState([]);
  const [page, setPage] = useState(initialFilters.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(initialFilters.rowsPerPage || 10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState(initialFilters.sortBy || "businessName");
  const [sortDirection, setSortDirection] = useState(initialFilters.sortDirection || "asc");
  const [totalRecordsSum, setTotalRecordsSum] = useState(0);
  const [totalVerifiedSum, setTotalVerifiedSum] = useState(0);
  const [totalNonValidiSum, setTotalNonValidiSum] = useState(0);
  const [totalDaClassificareSum, setTotalDaClassificareSum] = useState(0);

  const [filterStatus, setFilterStatus] = useState(initialFilters.status === undefined ? null : initialFilters.status);
  const [tableLoading, setTableLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { showSuccess, showLoading } = useUI();
  const { startVendorIndexPolling } = useVendorIndexPolling();

  // Stato per il Dialog del Credito
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [selectedVendorForCredit, setSelectedVendorForCredit] = useState(null);
  const [vendorCredit, setVendorCredit] = useState(null);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [creditDialogTab, setCreditDialogTab] = useState(0);

  // Stato per aggiungere credito
  const [addCreditAmount, setAddCreditAmount] = useState("");
  const [addingCredit, setAddingCredit] = useState(false);
  const [addCreditError, setAddCreditError] = useState(null);

  // Stato per transazioni
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);

  const fetchVendorBalance = async (vendorId) => {
    setLoadingCredit(true);
    setVendorCredit(null);
    try {
      const balance = await getCustomerBalance(vendorId);
      setVendorCredit(balance);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCredit(false);
    }
  };

  const fetchVendorTransactions = async (vendorId) => {
    setLoadingTransactions(true);
    setTransactionsError(null);
    try {
      const data = await getVendorTransactions(vendorId);
      setTransactions(data || []);
    } catch (error) {
      setTransactionsError(error.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleViewCredit = async (vendor) => {
    setSelectedVendorForCredit(vendor);
    setIsCreditDialogOpen(true);
    setCreditDialogTab(0);
    setAddCreditAmount("");
    setAddCreditError(null);
    setTransactions([]);
    await fetchVendorBalance(vendor.vendorListId);
  };

  const handleCreditDialogTabChange = (_, newValue) => {
    setCreditDialogTab(newValue);
    if (newValue === 1 && transactions.length === 0 && !loadingTransactions) {
      fetchVendorTransactions(selectedVendorForCredit.vendorListId);
    }
  };

  const handleAddCredit = async () => {
    if (!addCreditAmount || Number.isNaN(Number(addCreditAmount)) || Number(addCreditAmount) <= 0) return;
    setAddingCredit(true);
    setAddCreditError(null);
    try {
      await addVendorCredit(selectedVendorForCredit.vendorListId, Number(addCreditAmount));
      setAddCreditAmount("");
      showSuccess(`Credito di €${Number(addCreditAmount).toFixed(2)} aggiunto con successo!`);
      await fetchVendorBalance(selectedVendorForCredit.vendorListId);
    } catch (error) {
      setAddCreditError(error.message);
    } finally {
      setAddingCredit(false);
    }
  };

  // Stato per Azione Generica (Sospendi, Rifiuta, Elimina)
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedVendorForAction, setSelectedVendorForAction] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionTargetStatus, setActionTargetStatus] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [sendEmailChecked, setSendEmailChecked] = useState(true);
  const [includeReasonChecked, setIncludeReasonChecked] = useState(false);


  // Salvataggio filtri in sessionStorage al cambiamento
  useEffect(() => {
    const filters = {
      query: filterInput,
      status: filterStatus,
      page,
      rowsPerPage,
      sortBy,
      sortDirection
    };
    sessionStorage.setItem("ecommerce_filters", JSON.stringify(filters));
  }, [filterInput, filterStatus, page, rowsPerPage, sortBy, sortDirection]);

  // Stati per menu azioni (sospeso)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [selectedVendorForActions, setSelectedVendorForActions] = useState(null);

  // Stato per Impersonificazione
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const [selectedVendorForImpersonate, setSelectedVendorForImpersonate] = useState(null);

  // Helper per le operazioni asincrone con overlay
  const performAsyncAction = async (actionFn, cleanupFn, skipFetch = false, onSuccess) => {
    try {
      showLoading("Elaborazione in corso...");
      await actionFn();
      if (cleanupFn) cleanupFn();
      if (onSuccess) onSuccess();
      showSuccess("Operazione completata con successo!");
      if (!skipFetch) {
        await fetchData(filterInput, page, rowsPerPage, sortDirection, filterStatus);
      }
    } catch (error) {
      if (cleanupFn) cleanupFn();
      console.error("Async action failed:", error);
    }
  };

  // Calcolo centralizzato del numero totale di pagine
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const navigate = useNavigate();

  const handleOpen = (row) => {
    navigate('/gestione-ecommerce/modifica', { state: { vendor: row } });
  };

  const handleOpenActionModal = (vendor, type, targetStatus) => {
    setSelectedVendorForAction(vendor);
    setActionType(type);
    setActionTargetStatus(targetStatus);
    setActionReason("");
    setSendEmailChecked(true);
    setIncludeReasonChecked(false);
    setIsActionModalOpen(true);
  };

  const handleConfirmAction = () => {
    performAsyncAction(
      () => updateEcom({
        idVendor: selectedVendorForAction?.vendorListId,
        stato: actionTargetStatus,
        sendEmail: sendEmailChecked,
        reason: actionReason,
        includeReason: includeReasonChecked
      }),
      () => {
        setIsActionModalOpen(false);
        setSelectedVendorForAction(null);
        setActionReason("");
      },
      false,
      startVendorIndexPolling
    );
  };

  const handleOpenActionsMenu = (event, vendor) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedVendorForActions(vendor);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
    setSelectedVendorForActions(null);
  };

  const handleReactivate = (vendor) => {
    handleOpenActionModal(vendor, "Riattiva", "Attivo");
  };

  const handleAccept = (vendor) => {
    handleOpenActionModal(vendor, "Attiva", "Attivo");
  };

  const handleOpenImpersonateDialog = (vendor) => {
    setSelectedVendorForImpersonate(vendor);
    setIsImpersonateModalOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const fetchData = useCallback(async (searchQuery = "", currentPage = 0, currentRowsPerPage = 5, currentSortDirection = "asc", currentStatus = null) => {
    setTableLoading(true);
    setFetchError(false);
    try {
      const data = await getEcomByFilter(searchQuery, currentPage, currentRowsPerPage, currentSortDirection, currentStatus);
      if (Array.isArray(data)) {
        setEcommerceList(data);
        setTotalCount(currentPage * currentRowsPerPage + data.length + (data.length === currentRowsPerPage ? 1 : 0));
      } else if (data) {
        setEcommerceList(data.vendorSearchResponses || []);
        setTotalCount(data.countVendors || 0);
        setTotalRecordsSum(data.sumTotalRecords || 0);
        setTotalVerifiedSum(data.sumTotalVerified || 0);
        setTotalNonValidiSum(data.sumProdottiNonValidi || 0);
        setTotalDaClassificareSum(data.sumProdottiDaClassificare || 0);
      }
    } catch (error) {
      setFetchError(true);
      setErrorMessage(error.message);
    } finally {
      setTableLoading(false);
    }
  }, []);

  const lastParamsRef = useRef(null);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const query = filterInput.length >= 3 ? filterInput : "";
      const lastParams = lastParamsRef.current;

      const paramsChanged = query !== lastParams?.query ||
        page !== lastParams?.page ||
        rowsPerPage !== lastParams?.rowsPerPage ||
        sortDirection !== lastParams?.sortDirection ||
        filterStatus !== lastParams?.filterStatus;

      if (paramsChanged) {
        fetchData(query, page, rowsPerPage, sortDirection, filterStatus);
        lastParamsRef.current = { page, rowsPerPage, sortDirection, filterStatus, query };
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchData, filterInput, page, rowsPerPage, sortDirection, filterStatus]);

  const handleFilter = (e) => {
    e.preventDefault();
    if (filterRef.current) {
      filterRef.current.blur();
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterInput("");
    setFilterStatus(null);
    setSortBy("businessName");
    setSortDirection("asc");
    setPage(0);
  };

  const toggleCardExpansion = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };


  const renderSharedEmptyState = () => (
    <EmptyStateContent
      fetchError={fetchError}
      errorMessage={errorMessage}
      onRetry={() => fetchData(filterInput, 0, rowsPerPage, sortDirection, filterStatus)}
      onClearFilters={handleClearFilters}
      hasSearch={!!(filterInput || filterStatus)}
    />
  );

  const renderMobileEmptyState = () => {
    return (
      <Box sx={{
        p: 6,
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: 4,
        border: '2px dashed #e0e6ed',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        my: 2
      }}>
        {renderSharedEmptyState()}
      </Box>
    );
  };


  const renderMobileList = () => {
    return (
      <Stack spacing={2} sx={{ mb: 2 }}>
        {ecommerceList.map((row) => {
          const isExpanded = expandedCards[row.vendorListId];
          return (
            <Card key={row.vendorListId} elevation={0} sx={{
              borderRadius: '18px',
              border: '1px solid #e6ebf3',
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
                {/* Header: logo ambient + nome + stato */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <AmbientVendorLogo logo={row.logo} name={formatValue(row.tradeName)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <LogoTooltip logo={row.logo} name={formatValue(row.tradeName)}>
                      <Typography sx={{
                        color: '#0b3d87',
                        fontWeight: 800,
                        fontSize: '1.05rem',
                        lineHeight: 1.2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        cursor: vendorLogoSrc(row.logo) ? 'pointer' : 'inherit'
                      }}>
                        {formatValue(row.tradeName)}
                      </Typography>
                    </LogoTooltip>
                    <Chip
                      label={getStatusLabel(row)}
                      color={getStatusColor(row)}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.75, fontWeight: 700 }}
                    />
                  </Box>
                </Box>

                {/* Prodotti certificati */}
                <Box sx={{ mt: 1.75, backgroundColor: '#f8fafc', border: '1px solid #eef2f7', borderRadius: '12px', p: 1.25 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5, textAlign: 'center' }}>
                    Prodotti certificati / totali
                  </Typography>
                  {renderProductProgressBar(row.totalVerified, row.totalRecords)}
                </Box>

                {/* Info principali + dettagli espandibili */}
                <Box sx={{ mt: 1.5 }}>
                  <VendorInfoRow label="Referente" value={getReferenteName(row)} />
                  <VendorInfoRow label="Sito Web" value={formatValue(row.websiteUrl)} truncate />

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <VendorInfoRow label="Email" value={formatValue(row.email)} truncate />
                    <VendorInfoRow label="Email Pubblica" value={formatValue(row.pubEmail)} truncate />
                    <VendorInfoRow label="Telefono" value={formatValue(row.phoneNumber)} />
                    <VendorInfoRow label="Email Referente" value={formatValue(row.refEmail)} truncate />
                    <VendorInfoRow label="Telefono Referente" value={formatValue(row.refPhoneNumber)} />
                    <VendorInfoRow label="Ultimo import" value={formatDate(row.lastImportDate)} />
                  </Collapse>
                </Box>

                {/* Footer: espandi + azioni */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.75, pt: 1.25, borderTop: '1px solid #f0f2f5' }}>
                  <IconButton
                    onClick={() => toggleCardExpansion(row.vendorListId)}
                    sx={{
                      color: '#64748b',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s, background-color 0.2s',
                      backgroundColor: '#f8fafc',
                      "&:hover": { backgroundColor: '#f1f5f9' }
                    }}
                    size="small"
                  >
                    <ExpandMoreIcon />
                  </IconButton>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <VendorActionGroup
                      row={row}
                      isMobile={true}
                      navigate={navigate}
                      handlers={{
                        handleAccept,
                        handleOpen,
                        handleOpenActionModal,
                        handleOpenActionsMenu,
                        handleReactivate,
                        handleOpenImpersonateDialog,
                        handleViewCredit
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    );
  };

  const renderMobileCards = () => {
    if (!tableLoading && (fetchError || ecommerceList.length === 0)) {
      return renderMobileEmptyState();
    }
    return renderMobileList();
  };

  const searchAndFilters = (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "center",
        flexWrap: { xs: "wrap", md: "nowrap" },
        width: { xs: "100%", md: "auto" },
      }}
    >
      <form onSubmit={handleFilter} style={{ flex: "1 1 auto", minWidth: 0 }}>
        <TextField
          fullWidth
          inputRef={filterRef}
          placeholder="Cerca per ragione sociale, email, referente..."
          value={filterInput}
          onChange={(e) => {
            setFilterInput(e.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#0b3d87", opacity: 0.6 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "16px",
                backgroundColor: "#f8fafc",
                height: "54px",
                width: { xs: "100%", md: 320 },
                fontSize: "0.9rem",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  boxShadow:
                    "0 0 0 4px rgba(11, 61, 135, 0.08), 0 4px 12px rgba(11, 61, 135, 0.05)",
                  "& fieldset": { borderColor: "#0b3d87 !important", borderWidth: "2px" },
                },
                "& fieldset": { borderColor: "#e2e8f0 !important" },
              },
            },
          }}
        />
      </form>

      <FormControl sx={{ minWidth: 100, flex: { xs: "1 1 5%", md: "0 0 auto" } }}>
        <InputLabel sx={{ color: "#64748b", fontSize: "0.85rem" }}>Stato</InputLabel>
        <Select
          value={filterStatus === null ? "" : filterStatus}
          onChange={(e) => { 
            const v = e.target.value;
            setFilterStatus(v === "" ? null : v); 
            setPage(0); 
          }}
          label="Stato"
          sx={{
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
            height: "54px",
            fontSize: "0.85rem",
            transition: "all 0.2s",
            "&:hover": { backgroundColor: "#f1f5f9" },
            "&.Mui-focused": { backgroundColor: "white" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0 !important" },
          }}
        >
          <MenuItem value="">Tutti</MenuItem>
          <MenuItem value="In Attivazione">Registrato</MenuItem>
          <MenuItem value="Registrato">Verificato</MenuItem>
          <MenuItem value="Attivo">Attivo</MenuItem>
          <MenuItem value="Sospeso">Sospeso</MenuItem>
          <MenuItem value="Non Attivo">Disattivo</MenuItem>
        </Select>
      </FormControl>

      {(filterInput.length >= 3 || filterStatus !== null) && (
        <Tooltip title="Pulisci filtri" copyable={false}>
          <IconButton
            onClick={handleClearFilters}
            sx={{
              flexShrink: 0,
              backgroundColor: "#f1f5f9",
              color: "#475569",
              width: 54,
              height: 54,
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              "&:hover": { backgroundColor: "#e2e8f0" },
              transition: "all 0.2s",
            }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box sx={adminPageShellSx}>
      <AdminPageHeader
        title="Gestione E-Commerce"
        subtitle="Monitora e gestisci gli e-commerce registrati, gli stati e le sincronizzazioni"
        actions={isMobile ? undefined : searchAndFilters}
      />

      {isMobile && (
        <Box
          sx={{
            position: "sticky",
            top: 15,
            zIndex: 1100,
            mb: 2,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(16px) saturate(180%)",
            WebkitBackdropFilter: "blur(16px) saturate(180%)",
            borderRadius: "18px",
            p: 1,
            boxShadow: "0 8px 24px -10px rgba(31, 38, 135, 0.18)",
          }}
        >
          {searchAndFilters}
        </Box>
      )}

      <Box sx={{ position: 'relative', width: '100%', minHeight: '300px' }}>
        <TableLoadingOverlay loading={tableLoading} />

        {isMobile ? renderMobileCards() : (
          <EcommerceDesktopTable
            tableLoading={tableLoading} ecommerceList={ecommerceList} fetchError={fetchError}
            errorMessage={errorMessage} rowsPerPage={rowsPerPage} sortBy={sortBy}
            sortDirection={sortDirection} filterInput={filterInput} filterStatus={filterStatus}
            handleRequestSort={handleRequestSort}
            handleAccept={handleAccept} handleOpen={handleOpen} handleOpenActionModal={handleOpenActionModal}
            handleOpenActionsMenu={handleOpenActionsMenu} handleReactivate={handleReactivate}
            handleOpenImpersonateDialog={handleOpenImpersonateDialog}
            handleViewCredit={handleViewCredit}
            setFilterStatus={setFilterStatus} setPage={setPage} page={page} navigate={navigate}
            emptyStateComponent={renderSharedEmptyState()}
            headerTop={0}
          />
        )}
      </Box>

      {/* Barra inferiore: paginazione + statistiche, in fondo al contenuto (non sticky) */}
      <Box sx={{ mt: 2 }}>
        <EcommercePagination
          isMobile={isMobile}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          totalCount={totalCount}
          totalRecordsSum={totalRecordsSum}
          totalVerifiedSum={totalVerifiedSum}
          totalDaClassificareSum={totalDaClassificareSum}
          totalNonValidiSum={totalNonValidiSum}
        />
      </Box>

      <ScrollTopFab />

      {/* Menù e Modali */}
      <Menu
        anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={handleCloseActionsMenu}
        slotProps={{ paper: { elevation: 3, sx: { borderRadius: 2, minWidth: 150 } } }}
      >
        <MenuItem onClick={() => handleReactivate(selectedVendorForActions)}>
          <ListItemIcon><RestoreIcon sx={{ color: '#4caf50' }} /></ListItemIcon>
          <ListItemText primary="Riattiva" />
        </MenuItem>
        {getStatusLabel(selectedVendorForActions) !== "Disattivo" && (
          <MenuItem onClick={() => handleOpenActionModal(selectedVendorForActions, "Disattiva", "Non Attivo")}>
            <ListItemIcon><DeleteIcon sx={{ color: '#f44336' }} /></ListItemIcon>
            <ListItemText primary="Disattiva" />
          </MenuItem>
        )}
      </Menu>

      <ActionDialog
        open={isActionModalOpen} onClose={() => setIsActionModalOpen(false)} onConfirm={handleConfirmAction}
        title={`${actionType} Ecommerce`} actionType={actionType} vendorName={selectedVendorForAction?.tradeName}
        reason={actionReason} onReasonChange={setActionReason}
        emailChecked={sendEmailChecked} onEmailChange={setSendEmailChecked}
        includeReasonChecked={includeReasonChecked} onIncludeReasonChange={setIncludeReasonChecked}
      />

      <ImpersonateDialog
        open={isImpersonateModalOpen}
        onClose={() => setIsImpersonateModalOpen(false)}
        vendor={selectedVendorForImpersonate}
        performAsyncAction={performAsyncAction}
      />

      {/* Dialog Gestione Credito e Transazioni */}
      <Dialog
        open={isCreditDialogOpen}
        onClose={() => setIsCreditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '500px' } }}
      >
        <DialogTitle sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Gestione Finanziaria — {selectedVendorForCredit?.tradeName}</span>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={creditDialogTab} onChange={handleCreditDialogTabChange}>
            <Tab label="Credito" />
            <Tab label="Transazioni" />
          </Tabs>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {/* TAB 0: Credito */}
          {creditDialogTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Saldo Card */}
              <Box sx={{
                p: 3, borderRadius: 3,
                background: 'linear-gradient(135deg, #0b3d87 0%, #1565c0 100%)',
                color: '#fff', textAlign: 'center'
              }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 0.5 }}>Credito Corrente</Typography>
                {loadingCredit ? (
                  <CircularProgress size={32} sx={{ color: '#fff' }} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700, fontFamily: "Rubik" }}>
                    {typeof vendorCredit === "number" ? vendorCredit.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0,00"}€
                  </Typography>
                )}
              </Box>

              {/* Aggiungi Credito */}
              <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#0b3d87', mb: 2 }}>
                  Aggiungi Credito Manuale
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Inserisci l'importo da aggiungere al credito del vendor. Questa operazione aggiorna direttamente il saldo su Stripe.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label="Importo (€)"
                    type="number"
                    value={addCreditAmount}
                    onChange={(e) => setAddCreditAmount(e.target.value)}
                    inputProps={{ min: 0.01, step: 0.01 }}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddCredit}
                    disabled={addingCredit || !addCreditAmount || Number.isNaN(Number(addCreditAmount)) || Number(addCreditAmount) <= 0}
                    sx={{
                      borderRadius: '12px', height: '40px', px: 4,
                      backgroundColor: '#0b3d87',
                      '&:hover': { backgroundColor: '#092b6a' }
                    }}
                  >
                    {addingCredit ? <CircularProgress size={20} color="inherit" /> : "Aggiungi"}
                  </Button>
                </Box>
                {addCreditError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{addCreditError}</Alert>
                )}
              </Box>
            </Box>
          )}

          {/* TAB 1: Transazioni */}
          {creditDialogTab === 1 && (
            <Box>
              <TransactionsTabContent
                loading={loadingTransactions}
                error={transactionsError}
                transactions={transactions}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchVendorTransactions(selectedVendorForCredit.vendorListId)}
                  disabled={loadingTransactions}
                  sx={{ borderRadius: '12px' }}
                >
                  Aggiorna
                </Button>
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsCreditDialogOpen(false)}
            variant="contained"
            sx={{
              borderRadius: '20px',
              backgroundColor: '#0b3d87',
              color: 'white',
              px: 4,
              '&:hover': { backgroundColor: '#092b6a' }
            }}
          >
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const EcommerceDesktopTable = ({
  tableLoading, ecommerceList, fetchError, errorMessage, rowsPerPage,
  sortBy, sortDirection, filterInput, filterStatus, handleRequestSort,
  handleAccept, handleOpen, handleOpenActionModal,
  handleOpenActionsMenu, handleReactivate, handleOpenImpersonateDialog, handleViewCredit, fetchData, setFilterInput, setFilterStatus, setPage, page, navigate,
  emptyStateComponent, headerTop = 0
}) => {
  return (
    <AdminMergedTable
      tableLoading={tableLoading}
      tableLayout="fixed"
      head={(
        <TableHead sx={mergedTableHeadSx(0)}>
          <TableRow>
            <TableCell align="center">
              <TableSortLabel
                active={sortBy === 'businessName'} direction={sortBy === 'businessName' ? sortDirection : 'asc'}
                onClick={() => handleRequestSort('businessName')}
                sx={{ color: "white !important", width: "100%", justifyContent: "center", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
              >
                <Box sx={{ display: 'inline-block', lineHeight: 1.1, textAlign: 'center' }}>
                  Ragione<br />Sociale
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Sito Web</TableCell>
            <TableCell align="center">Referente</TableCell>
            <TableCell align="center">Email</TableCell>
            <TableCell align="center">Telefono</TableCell>
            <TableCell align="center">Ultimo import</TableCell>
            <TableCell align="center" sx={{ width: 120 }}>
              <Box sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle',
                lineHeight: 1.2,
                // Più compatto del resto delle intestazioni: questo testo è su due righe
                // dentro una colonna stretta (170px), quindi riduco corpo e spaziatura
                // per evitare che il maiuscoletto spaziato venga tagliato.
                fontSize: '0.7rem',
                letterSpacing: '0.02em',
                fontWeight: 700,
                color: 'white'
              }}>
                <Box sx={{ pb: 0.5, borderBottom: '1px solid rgba(255, 255, 255, 0.4)', width: '100%', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  Prodotti Certificati
                </Box>
                <Box sx={{ pt: 0.5, width: '100%', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  Prodotti Totali
                </Box>
              </Box>
            </TableCell>
            <TableCell align="center" sx={{ width: 90, px: 1 }}>Stato</TableCell>
            <TableCell align="center" sx={{ width: 220, px: 1 }}>Azioni</TableCell>
          </TableRow>
        </TableHead>
      )}
      body={(
        <TableBody>
          {(!tableLoading && (fetchError || ecommerceList.length === 0)) ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: '250px' }}>
                  {emptyStateComponent}
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            ecommerceList.map((row, index) => (
              <TableRow
                key={row.vendorListId}
                sx={mergedTableRowSx(index)}
              >
                <TableCell align="center">
                  {vendorLogoSrc(row.logo) ? (
                    <LogoTooltip logo={row.logo} name={formatValue(row.tradeName)}>
                      <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle', cursor: 'pointer' }}>
                        {formatValue(row.tradeName)}
                      </Box>
                    </LogoTooltip>
                  ) : (
                    <TruncatedCellContent value={row.tradeName} />
                  )}
                </TableCell>
                <TableCell align="center"><TruncatedCellContent value={row.websiteUrl} /></TableCell>
                <TableCell align="center">
                  <ReferenteCell name={row.referringName} surname={row.referringSurname} />
                </TableCell>
                <TableCell align="center"><TruncatedCellContent value={row.email} /></TableCell>
                <TableCell align="center"><TruncatedCellContent value={row.phoneNumber} /></TableCell>
                <TableCell align="center">
                  <LastImportCell value={row.lastImportDate} />
                </TableCell>
                <TableCell align="center">
                  {renderProductProgressBar(row.totalVerified, row.totalRecords)}
                </TableCell>
                <TableCell align="center" sx={{ px: 1 }}>
                  <Chip
                    label={getStatusLabel(row)}
                    color={getStatusColor(row)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: "8px", borderWidth: "1.5px", fontSize: "0.75rem" }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap', px: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap', gap: 0.5 }}>
                    <VendorActionGroup
                      row={row}
                      isMobile={false}
                      navigate={navigate}
                      handlers={{
                        handleAccept,
                        handleOpen,
                        handleOpenActionModal,
                        handleOpenActionsMenu,
                        handleReactivate,
                        handleOpenImpersonateDialog,
                        handleViewCredit
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}

        </TableBody>
      )}
    />
  );
};

EcommerceDesktopTable.propTypes = {
  tableLoading: PropTypes.bool.isRequired,
  ecommerceList: PropTypes.array.isRequired,
  fetchError: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  rowsPerPage: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  filterInput: PropTypes.string.isRequired,
  filterStatus: PropTypes.string,
  handleRequestSort: PropTypes.func.isRequired,
  handleAccept: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired,
  handleOpenActionModal: PropTypes.func.isRequired,
  handleOpenActionsMenu: PropTypes.func.isRequired,
  handleReactivate: PropTypes.func.isRequired,
  handleOpenImpersonateDialog: PropTypes.func.isRequired,
  handleViewCredit: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  setFilterInput: PropTypes.func.isRequired,
  setFilterStatus: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  navigate: PropTypes.func.isRequired,
  emptyStateComponent: PropTypes.node,
  headerTop: PropTypes.number
};
const EcommercePagination = ({
  isMobile, rowsPerPage, setRowsPerPage, page, setPage, totalPages, totalCount,
  totalRecordsSum, totalVerifiedSum, totalDaClassificareSum, totalNonValidiSum
}) => (
  <AdminTablePagination
    isMobile={isMobile}
    rowsPerPage={rowsPerPage}
    setRowsPerPage={setRowsPerPage}
    page={page}
    setPage={setPage}
    totalPages={totalPages}
    countLabel="Ecommerce"
    countValue={totalCount}
  >
    {/* Statistiche unite alla barra di paginazione */}
    {!isMobile && <Divider sx={{ mt: 2, mb: 0 }} />}
    <EcommerceStats
      totalRecordsSum={totalRecordsSum}
      totalVerifiedSum={totalVerifiedSum}
      totalDaClassificareSum={totalDaClassificareSum}
      totalNonValidiSum={totalNonValidiSum}
      isMobile={isMobile}
    />
  </AdminTablePagination>
);

EcommercePagination.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  totalRecordsSum: PropTypes.number.isRequired,
  totalVerifiedSum: PropTypes.number.isRequired,
  totalDaClassificareSum: PropTypes.number.isRequired,
  totalNonValidiSum: PropTypes.number.isRequired
};

export default GestioneEcommerce;
